import { Nullable } from 'global';
import { v4 as uuidv4 } from 'uuid';
import { ListingForm } from 'components/shared/TableListing';
import { UserProfile } from '@trnsact/trnsact-shared-types';
import { formatSnakeToCamelCaseLabel } from 'utils/formatters';

import { CreateBPFTemplateInput, Template } from '../api';
import { initialStagesConstructorFormValue, stagesConstructorFormRow } from '../constants';
import { Recipient, RecipientRole, StagesFormValues, TemplateConstructorMode, TemplateFormValues } from '../types';

class StagesMapper {
  processStageFields(stage: StagesFormValues): CreateBPFTemplateInput['stages'][number]['stageFields'] {
    return stage.listOfAttributes.map(attribute => {
      if (attribute.field === 'checklist') {
        const { field, ...rest } = attribute;

        return { field, value: [{ ...rest, id: rest.id ?? uuidv4() }] };
      }

      if (attribute.field === 'document') {
        const { field, ...rest } = attribute;

        return { field, value: [rest] };
      }

      throw new Error(`Unsupported attribute type in stage: ${stage.name}`);
    });
  }

  processFormValuesToCreateOrUpdateDto(input: {
    accountId: string;
    mode: TemplateConstructorMode;
    templateValues: TemplateFormValues;
    stagesValues: ListingForm<StagesFormValues>;
  }): CreateBPFTemplateInput {
    const {
      mode,
      accountId,
      stagesValues,
      templateValues: { applyRules, templateName, isActive, businessProcess },
    } = input;

    return {
      accountId,
      name: templateName,
      isActive: isActive,
      departmentId: businessProcess,
      applyRules: {
        ...(applyRules.paymentType ? { paymentType: applyRules.paymentType } : {}),
        ...(applyRules.applyingFor?.trim() ? { applyingFor: applyRules.applyingFor.trim() } : {}),
        ...(applyRules.equipmentCondition ? { equipmentCondition: applyRules.equipmentCondition } : {}),
      },
      stages: stagesValues.bodyFields.map((stage, index) => ({
        ordinal: index,
        name: stage.name,
        completionRules: {},
        winProbability: stage.winProbability,
        stageFields: this.processStageFields(stage),
        ...(stage.id && mode === 'edit' ? { id: stage.id } : {}),
        notificationRules: {
          notificationEvents: ['STAGE_COMPLETED'],
          roleRecipients: stage.recipients.filter(({ role }) => !!role).map(({ role }) => ({ role })),
          userProfileRecipients: stage.recipients
            .filter(({ userProfileId }) => !!userProfileId)
            .map(({ userProfileId }) => ({ userProfileId })),
        },
      })),
    };
  }

  processTemplateToFormValues(template: Template): TemplateFormValues {
    return {
      ...initialStagesConstructorFormValue,
      isActive: template.isActive,
      templateName: template.name,
      businessProcess: template.department.id,
      applyRules: {
        ...initialStagesConstructorFormValue.applyRules,
        ...(template.applyRules ?? {}),
      },
    };
  }

  processTemplateToConstructorFormValues(
    template: Nullable<Template>,
    relatedUsers?: Map<string, UserProfile>
  ): StagesFormValues[] {
    if (!template) return [{ ...stagesConstructorFormRow }];

    return template.stages.map(
      ({ businessProcessFlowStageId, name, stageFields, notificationRules, winProbability }) => {
        return {
          name,
          winProbability,
          id: businessProcessFlowStageId,
          recipients: notificationRules.reduce<Recipient[]>((acc, { userProfileRecipients, roleRecipients }) => {
            userProfileRecipients.forEach(({ userProfileId }) => {
              acc.push({
                userProfileId,
                role: undefined,
                name: relatedUsers ? relatedUsers.get(userProfileId)?.fullName || 'Unknown User' : 'Unknown User',
              });
            });

            roleRecipients.forEach(({ role }) => {
              acc.push({
                userProfileId: undefined,
                role: role as RecipientRole,
                name: formatSnakeToCamelCaseLabel(role),
              });
            });

            return acc;
          }, []),
          listOfAttributes: (Array.isArray(stageFields) ? stageFields : []).reduce<
            StagesFormValues['listOfAttributes']
          >((acc, stageField) => {
            if (stageField.field === 'checklist') {
              stageField.value.forEach(fieldData => {
                acc.push({
                  ...fieldData,
                  field: stageField.field,
                  value: fieldData.value ?? false,
                  id: fieldData.id ?? uuidv4(),
                });
              });
            }

            if (stageField.field === 'document') {
              stageField.value.forEach(fieldData => {
                acc.push({
                  ...fieldData,
                  field: stageField.field,
                  value: fieldData.value ?? false,
                });
              });
            }

            return acc;
          }, []),
        };
      }
    );
  }
}

export const stagesMapper = new StagesMapper();
